/**
 * AgentProcessLibrary — account process library.
 *
 * Rendered as the "Processes" tab inside AgentsView (moved off the left nav in
 * round-2 UX — the tab is shown only when the dev toggle is on, gated by the
 * caller). Lists seeded system defaults (the Global Library) and account
 * customs (My Library) with:
 *   - a Global / My / All library filter (chips),
 *   - a client-side substring search box, and
 *   - client-side pagination
 * mirroring the WorkspacesView pattern (useFilteredList + usePagination +
 * DataList). System rows are read-only with a copy-to-my-library action;
 * custom rows open the editor at /agent-processes/:id.
 *
 * This is a block component (no full-height chrome of its own) so it sits
 * inside the AgentsView tab body, whose surrounding ListDetailLayout owns the
 * scroll container. Editing/creating still happens on the standalone
 * /agent-processes/{new,:id} editor routes.
 */

import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CopyIcon, DataList, TrashIcon } from '@localagent/ui/components/data-list';
import { ListAddButton, ListFilterMenu } from '@localagent/ui/components/list-filter-menu';
import { useFilteredList, type FilterValue } from '@localagent/ui/hooks/use-filtered-list';
import { usePagination } from '@localagent/ui/hooks/use-pagination';
import type { AgentProcess } from '@localagent/sdk';
import {
  useAgentProcesses,
  useCopyToAccount,
  useDeleteAgentProcess,
} from '../hooks/useAgentProcesses';

// Library scope filter: '' = all, 'global' = seeded system rows, 'mine' =
// account customs. useFilteredList only applies filterFns for non-empty values,
// so '' naturally means "no filter" (All).
type LibraryScope = '' | 'global' | 'mine';

const processSearchFn = (p: AgentProcess, query: string): boolean => {
  const q = query.toLowerCase();
  return (
    p.name.toLowerCase().includes(q) ||
    (p.description?.toLowerCase().includes(q) ?? false)
  );
};

const processFilterFns: Record<string, (p: AgentProcess, value: FilterValue) => boolean> = {
  library: (p, value) => {
    if (value === 'global') return p.is_system;
    if (value === 'mine') return !p.is_system;
    return true;
  },
};

const LIBRARY_CHIPS: Array<{ value: LibraryScope; label: string; testid: string }> = [
  { value: '', label: 'All', testid: 'process-filter-all' },
  { value: 'global', label: 'Global Library', testid: 'process-filter-global' },
  { value: 'mine', label: 'My Library', testid: 'process-filter-mine' },
];

export function AgentProcessLibrary() {
  const navigate = useNavigate();
  const { data: processes, isLoading, error } = useAgentProcesses();
  const copyToAccount = useCopyToAccount();
  const deleteProcess = useDeleteAgentProcess();

  // Newest-first so a just-created/copied process lands on page 1 (system
  // rows are seeded long ago and sink to later pages — the Global/My chips are
  // how users focus, not sort order). Mirrors WorkspacesView's rationale.
  const sortedProcesses = useMemo(
    () =>
      [...(processes ?? [])].sort((a, b) => {
        const at = a.created_at ? Date.parse(a.created_at) : 0;
        const bt = b.created_at ? Date.parse(b.created_at) : 0;
        return bt - at;
      }),
    [processes],
  );

  const { filteredItems, searchValue, onSearchChange, filterValues, onFilterChange } =
    useFilteredList({ items: sortedProcesses, searchFn: processSearchFn, filterFns: processFilterFns });

  const libraryScope = (filterValues.library as LibraryScope | undefined) ?? '';

  const pagination = usePagination({
    totalItems: filteredItems.length,
    pageSize: 10,
    pageSizeOptions: [10, 25, 50, 100],
  });

  // Return to page 1 whenever the search or library filter changes.
  useEffect(() => {
    pagination.reset();
  }, [searchValue, libraryScope]); // eslint-disable-line react-hooks/exhaustive-deps

  const pagedItems = filteredItems.slice(pagination.startIndex, pagination.endIndex);

  const renderItem = (process: AgentProcess) => (
    <div
      data-testid={`process-item-${process.id}`}
      className="flex items-center gap-3 py-2"
    >
      <button
        type="button"
        className="min-w-0 flex-1 text-left"
        onClick={() => { void navigate(`/agent-processes/${process.id}`); }}
      >
        <div className="flex items-center gap-2">
          <span className="font-medium">{process.name}</span>
          {process.is_system && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-micro font-medium text-muted-foreground">
              system
            </span>
          )}
          {process.definition.graph.nodes.some((n) => n.type === 'dynamic') && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-micro text-muted-foreground">
              dynamic
            </span>
          )}
          {process.definition.checks_goal && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-micro text-muted-foreground">
              checks goal
            </span>
          )}
        </div>
        {process.description && (
          <p className="mt-0.5 truncate text-body text-muted-foreground">{process.description}</p>
        )}
      </button>
    </div>
  );

  return (
    <div data-testid="agent-processes-view">
      {error && (
        <div className="mb-2 rounded-md bg-destructive/10 p-3 text-body text-destructive">
          Failed to load processes: {error.message}
        </div>
      )}
      {deleteProcess.error && (
        <div
          data-testid="process-delete-error"
          className="mb-2 rounded-md bg-destructive/10 p-3 text-body text-destructive"
        >
          Delete failed: {deleteProcess.error.message}
        </div>
      )}
      {copyToAccount.error && (
        <div
          data-testid="process-copy-error"
          className="mb-2 rounded-md bg-destructive/10 p-3 text-body text-destructive"
        >
          Copy failed: {copyToAccount.error.message}
        </div>
      )}

      <DataList<AgentProcess>
        headerLeading={
          <ListFilterMenu
            testId="process-filters"
            filters={[
              {
                key: 'library',
                label: 'Library',
                testId: 'process-library-filter',
                value: libraryScope,
                options: LIBRARY_CHIPS.map((chip) => ({ value: chip.value, label: chip.label, testId: chip.testid })),
                onChange: (value) => onFilterChange('library', value),
              },
            ]}
          />
        }
        itemActions={(process) =>
          process.is_system
            ? [
                {
                  label: 'Copy to My Library',
                  icon: <CopyIcon />,
                  testId: `process-copy-${process.id}`,
                  onClick: () => {
                    copyToAccount.mutate({
                      process_id: process.id,
                      new_name: `${process.name} (copy)`,
                    });
                  },
                },
              ]
            : [
                {
                  label: 'Delete',
                  icon: <TrashIcon />,
                  variant: 'destructive' as const,
                  testId: `process-delete-${process.id}`,
                  onClick: () => deleteProcess.mutate(process.id),
                },
              ]
        }
        items={pagedItems}
        renderItem={renderItem}
        getItemId={(p) => p.id}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        searchPlaceholder="Search processes..."
        page={pagination.page}
        totalPages={pagination.totalPages}
        onPageChange={pagination.onPageChange}
        totalItems={filteredItems.length}
        pageSize={pagination.pageSize}
        pageSizeOptions={pagination.pageSizeOptions}
        onPageSizeChange={pagination.onPageSizeChange}
        isLoading={isLoading}
        headerActions={
          <ListAddButton label="New process" testId="process-new-button" onClick={() => { void navigate('/agent-processes/new'); }} />
        }
        emptyState={
          <p className="text-body text-muted-foreground">
            {libraryScope === 'mine'
              ? 'No processes in My Library yet.'
              : libraryScope === 'global'
                ? 'No processes in the Global Library.'
                : searchValue
                  ? 'No processes match your search.'
                  : 'No processes yet.'}
          </p>
        }
      />
    </div>
  );
}
